import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import ApiService from 'services/ApiService';
import module from 'pages/css/moduleStyle.module.css';
import { styled } from '@mui/material/styles';
import styles from 'pages/css/sgnlOp.module.css';
import Button from '@mui/material/Button';
import { StyledIconCctvBtn } from 'components/button';
import { useTranslation } from 'react-i18next';
const TrafficTotalContext = createContext(null);
const SelectedItemContext = createContext(null);

export function SwItsEmergVehTotalProvider({ children, startDate, endDate }) {
    const [currentData, setCurrentData] = useState();
    const [selectedItem, setSelectedItem] = useState(null);
    const lastSelectionRef = useRef(null);

    const fetchData = async () => {
        if (!startDate || !endDate) return;

        try {
            const response = await ApiService.get(
                `newdashboard/sgnloprt/GetEmergVehLog?sdtm=${startDate}&edtm=${endDate}`
            );
            if (response.status === 200) {
                const data = response.data;

                const vehList = [...(data.veh || [])];
                const runningIndex = vehList.findIndex(v => v.runYN === 'Y');
                if (runningIndex > -1) {
                    const [runningVeh] = vehList.splice(runningIndex, 1);
                    vehList.unshift(runningVeh);
                }

                setCurrentData({
                    resultData: {
                        ...data,
                        veh: vehList,
                    }
                });

                if (vehList.length > 0) {
                    const runningVeh = vehList[0];
                    const currentVehId = `${runningVeh.deviceId}-${runningVeh.departureTime}`;

                    // 사용자 선택 후 30초 동안은 자동 선택 방지
                    const isUserSelection = lastSelectionRef.current && (Date.now() - lastSelectionRef.current < 25000);

                    if (!isUserSelection && (!selectedItem || selectedItem.id !== currentVehId)) {
                        const isComplete = runningVeh.runYN === 'N';

                        const formatSeconds = (seconds) => {
                            const min = Math.floor(seconds / 60);
                            const sec = seconds % 60;
                            return `${min}분 ${sec}초`;
                        };

                        const formatDepartureTime = (datetime) => {
                            if (!datetime) return '';
                            const date = new Date(datetime);
                            return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, '0')}월 ${String(date.getDate()).padStart(2, '0')}일 ${String(date.getHours()).padStart(2, '0')}시 ${String(date.getMinutes()).padStart(2, '0')}분`;
                        };

                        const truncateText = (text, maxLength = 13) => {
                            return text?.length > maxLength ? text.slice(0, maxLength) + '...' : text;
                        };

                        setSelectedItem({
                            id: currentVehId,
                            title: runningVeh.serviceNm,
                            titleRow: truncateText(runningVeh.serviceNm),
                            state: isComplete ? '운행완료' : '운행중',
                            complete: isComplete,
                            stDate: formatDepartureTime(runningVeh.departureTime),
                            address: `${runningVeh.startNm} → ${runningVeh.endNm}`,
                            expect: formatSeconds(runningVeh.estimatedTime),
                            arrive: isComplete ? formatSeconds(runningVeh.travelTime) : '운행중',
                            vehicleNum: runningVeh.deviceId,
                            location: runningVeh.location,
                            reductionRate: runningVeh.reductionRate,
                            ixrList: runningVeh.ixrNmList,
                            ixrNumList: runningVeh.ixrList,
                            sgnlLIist: runningVeh.sgnlLIist,
                            stationNm: runningVeh.stationNm,
                            nowIxrList: runningVeh.nowIxrList,
                            nowIxrNmList: runningVeh.nowIxrNmList,
                            length : runningVeh.routeLength
                        });

                        lastSelectionRef.current = null;
                    }
                }
            }
        } catch (error) {
            console.error("데이터 조회 중 오류 발생:", error);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, [startDate, endDate]);

    return (
        <TrafficTotalContext.Provider value={currentData}>
            <SelectedItemContext.Provider value={{ 
                selectedItem, 
                setSelectedItem: (item) => {
                    setSelectedItem(item);
                    lastSelectionRef.current = Date.now();
                }
            }}>
                {children}
            </SelectedItemContext.Provider>
        </TrafficTotalContext.Provider>
    );
}

const removeBracketContent = (text) => {
    return text?.replace(/\s*\(.*?\)/g, '') || '';
};

export function BodyData() {
    const currentData = useContext(TrafficTotalContext);
    const { selectedItem, setSelectedItem } = useContext(SelectedItemContext);

    const listContainerRef = useRef(null);
    const prevSelectedId = useRef(null);

    // 선택 항목 변경 시 스크롤 처리
    useEffect(() => {
        if (listContainerRef.current && selectedItem?.id !== prevSelectedId.current) {
            const activeElement = listContainerRef.current.querySelector(`.${styles.active}`);
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }
            prevSelectedId.current = selectedItem?.id;
        }
    }, [selectedItem]);


    const formatSeconds = (seconds) => {
        const min = Math.floor(seconds / 60);
        const sec = seconds % 60;
        return `${min}분 ${sec}초`;
    };

    const handleClick = (list) => {
        setSelectedItem(list);  // 클릭한 항목만 선택되게 업데이트
    };

    const formatDepartureTime = (datetime) => {
        if (!datetime) return '';
        const date = new Date(datetime);
        return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, '0')}월 ${String(date.getDate()).padStart(2, '0')}일 ${String(date.getHours()).padStart(2, '0')}시 ${String(date.getMinutes()).padStart(2, '0')}분`;
    };

    const truncateText = (text, maxLength = 13) => {
        if (!text) return '';
        return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    };

    const resultList = currentData?.resultData?.veh?.map((veh) => {
        const isComplete = veh.runYN === 'N';

        const id = `${veh.deviceId}-${veh.departureTime}`;

        return {
            id,  // id 포함
            title: veh.serviceNm,
            titleRow: truncateText(veh.serviceNm),
            state: isComplete ? '운행완료' : '운행중',
            complete: isComplete,
            stDate: formatDepartureTime(veh.departureTime),
            address: `${removeBracketContent(veh.startNm)} → ${removeBracketContent(veh.endNm)}`,
            expect: formatSeconds(veh.estimatedTime),
            arrive: isComplete ? formatSeconds(veh.travelTime) : '운행중',
            startDate: veh.departureTime?.split('T')[0],
            vehicleNum: veh.deviceId,
            location: veh.location,
            length: veh.routeLength,
            reductionRate: veh.reductionRate,
            ixrList: veh.ixrNmList,
            ixrNumList: veh.ixrList,
            sgnlLIist: veh.sgnlLIist,
            stationNm: veh.stationNm,
            nowIxrList: veh.nowIxrList,
            nowIxrNmList: veh.nowIxrNmList
        };
    }) || [];

    if (!currentData || resultList.length === 0) {
        return (
            <ul className={`${styles.listWrap} flx_col gap20`}>
                <li>데이터가 없습니다 !!!</li>
            </ul>
        );
    }

    return (

        <ul className="listWrap flx_col gap20">
            {resultList.map((list) => {
                const isSelected = selectedItem?.id === list.id;

                return (
                    <li
                        key={list.id}
                        className={`${styles.selectList} ${isSelected ? styles.active : ''}`}
                    >
                        <button type="button" className="flx a_center" onClick={() => handleClick(list)}>
                            <div className={`${styles.state} ${list.complete && styles.complete} flx a_center j_center`}>
                                {list.state}
                            </div>
                            <div className={`${styles.address} flx flx_col a_flx_st`}>
                                <span className={`${module.ft32} ${module.ftMedium}`}>[ {list.stDate} ] {list.titleRow}</span>
                                <span className={`${module.ftRegular} ${module.white80}`}>{list.address}</span>
                            </div>
                            <div className={`${styles.time} flx flx_col a_flx_end`}>
                                <span className={`${module.ftMedium}`}>예측시간 : {list.expect}</span>
                                <span className={`${styles.safe}`}>도착시간: {list.arrive}</span>
                            </div>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

// 총 운행결과 
export function HeaderData() {
    const currentData = useContext(TrafficTotalContext);
    if (!currentData) return null;
    const pointResult = currentData
        ? [
            {
                title: '운행중',
                data: currentData.resultData.runVehCount,
                unit: '건',
                color: 'alert50'
            },
            {
                title: '운행완료',
                data: currentData.resultData.stopVehCount,
                unit: '건',
                color: 'white50'
            },
            {
                title: '전체 단축율',
                data: currentData.resultData.totalReductionRate,
                unit: '%',
                color: 'pr50'
            }
        ]
        : [];
    return (
        <>
            <ul className={`${styles.point} flx j_center innerBg`}>
                {pointResult.map((list) => {
                    let color = '#E23A56';
                    if (list.title === '운행완료') color = 'var(--white-50)';
                    else if (list.title === '전체 단축율') color = 'var(--safe)';

                    return (
                        <li key={list.title}>
                            <div className={`${module.BoxSubTit}`}>{list.title}</div>
                            <div className="flx a_flx_end">
                                <span className='nbr' style={{ color }} >{list.data?.toLocaleString()}</span>
                                <span className='subTxt'>{list.unit}</span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

export function BodyListDetail() {
    const { selectedItem } = useContext(SelectedItemContext);
    console.log('selectedItem :::', selectedItem)
    const scrollContainerRef = useRef(null);
    const activeItemRefs = useRef({});
    const { t, i18n } = useTranslation();
    useEffect(() => {
        if (selectedItem && scrollContainerRef.current) {
            const signalValues = selectedItem.sgnlLIist?.split(',').map(Number) || [];
            const intersectionNames = selectedItem.ixrList?.split(',') || [];

            const signalState2 = intersectionNames.map((name, idx) => ({
                title: name,
                control: signalValues[idx] === 1 ? '중' : '전',
                id: `signal-${idx}`,
                isActive: signalValues[idx] === 1
            }));

            // 제어 중인(값이 1인) 첫 번째 항목 찾기
            const activeItemIndex = signalState2.findIndex(item => item.isActive);

            if (activeItemIndex !== -1) {
                const activeItemId = signalState2[activeItemIndex].id;
                const activeItemElement = activeItemRefs.current[activeItemId];

                if (activeItemElement) {
                    // 해당 항목으로 스크롤
                    activeItemElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        }
    }, [selectedItem]);

      useEffect(() => {
        if (selectedItem && scrollContainerRef.current) {
            // 완료 상태 시 최상단으로 스크롤
            if (selectedItem.complete) {
                scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            // 활성화된 교차로 찾기
            const activeIndex = signalState2.findIndex(item => item.isActive);
            if (activeIndex !== -1) {
                const activeId = `signal-${activeIndex}`;
                const activeElement = activeItemRefs.current[activeId];
                
                activeElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [selectedItem]);

    if (!selectedItem) return <div>선택된 항목이 없습니다.</div>;
    if (selectedItem.complete) {
        selectedItem.sgnlLIist = selectedItem.sgnlLIist
            ?.split(',')
            .map(val => val === '1' ? '0' : val)
            .join(',');

        selectedItem.nowIxrList = "";
        selectedItem.nowIxrNmList = "";
    }

    function smartSplit(str) {
        const result = [];
        let current = '';
        let insideParen = false;

        for (let i = 0; i < str.length; i++) {
            const char = str[i];

            if (char === '(') insideParen = true;
            if (char === ')') insideParen = false;

            if (char === ',' && !insideParen) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        if (current.trim()) result.push(current.trim());
        return result;
    }

    const intersectionNames = selectedItem.ixrList ? smartSplit(selectedItem.ixrList) : [];
    const intersectionNum = selectedItem.ixrNumList?.split(',') || [];
    const signalValues = selectedItem.sgnlLIist?.split(',').map(Number) || [];

    const nowIxrNmList = selectedItem.nowIxrNmList?.split(',') || [];
    const currentIxrName = nowIxrNmList[0]; // 차량 마커를 표시할 지점 이름

    const defaultData = [
        {
            title: '기존 예상 도착시간',
            data: selectedItem.expect,
            color: 'white50'
        },
        {
            title: selectedItem.complete ? '도착시간' : '신호 제어 예상 도착시간',
            data: selectedItem.arrive,
        },
        {
            title: selectedItem.complete ? '단축율' : '전체 예상 단축율',
            data: (!selectedItem.complete && Number(selectedItem.reductionRate) === 0) ? '-' : selectedItem.reductionRate,
            unit: selectedItem.complete ? '%' : (Number(selectedItem.reductionRate) === 0 ? '' : '%'),
            class: true
        }
    ];

    const defaultData2 = [
        {
            title: '차량번호',
            data: selectedItem.vehicleNum || '알 수 없음'
        },
        {
            title: '관할기관',
            data: selectedItem.stationNm || '알 수 없음'
        },
        {
            title: '목적지',
            data: selectedItem.title || '알 수 없음'
        },
        {
            title: '출발시간',
            data: selectedItem.stDate || '알 수 없음'
        },
        {
            title: '운행경로',
            data: selectedItem.address || '알 수 없음'
        },
        {
            title: '운행거리',
            data: `${selectedItem.length}km` || '알 수 없음'
        }
    ];
    const nowIxrList = selectedItem.nowIxrList?.split(',') || []; // 번호 기준으로 비교

    const signalState2 = intersectionNames.map((name, idx) => {
        const signal = signalValues[idx];
        const ixrCode = intersectionNum[idx]; // 번호로 판단
        const isNow = nowIxrList.includes(ixrCode); // 번호 기준 비교

        let controlStatus = '전';

        if (signal === 1) {
            controlStatus = '중'; // 제어 중이면 무조건 중
        } else if (!isNow) {
            controlStatus = '완료'; // 제어하지 않았고, 현재 지점에 없다면 완료
        }

        return {
            title: name,
            control: controlStatus,
            id: `signal-${idx}`,
            isActive: controlStatus === '중',
            num: ixrCode
        };
    });

    const totalComplete = signalState2.filter(item => item.control === '완료').length;
    const totalDuring = signalState2.filter(item => item.control === '중').length;
    const totalBefore = signalState2.filter(item => item.control === '전').length;

    const signalState = [
        {
            title: '제어완료',
            data: totalComplete,
            unit: '개',
            color: 'white50'
        },
        {
            title: '제어 중',
            data: totalDuring,
            unit: '개',
            color: 'caution'
        },
        {
            title: '제어 전',
            data: totalBefore,
            unit: '개',
            color: 'white50'
        },
    ];

    return (
        <div className={`${module.gap40} flx flx_col inner L_l`}>
            <div className={`${module.gap40} titWrap flx a_center`}>
                <h2 className="boxTit" style={{ color: 'var(--iv-02)' }}>{selectedItem.title}</h2>
                <div className={`${styles.state} ${selectedItem.complete && styles.complete} flx a_center j_center`}>
                    {selectedItem.state}
                </div>
            </div>

            <div className={`${module.gap60} flx flx_1`} style={{ height: 'calc(100% - 68px - 40px)' }}>
                {/* 기본정보 */}
                <div className={`${module.wFull} ${module.gap20} flx flx_col`}>
                    <h3 className={`${module.subTitle} subTitle${i18n.language === 'en' ? '_en' : ''}`}>
                        {t('EmergencyVehicleProfile')}
                    </h3>
                    <div className={`${styles.infoBox} ${module.gap40} flx flx_col`}>
                        <ul className={`${styles.point} flx j_center innerBg`}>
                            {defaultData.map((list) => (
                                <li key={list.title}>
                                    <div className={`${module.BoxSubTit}`}>{list.title}</div>
                                    <div className="flx a_flx_end">
                                        <span className={`${styles[list.color]} ${module.ft54}`}>{list.data}</span>
                                        <span className='subTxt' style={{ marginBottom: '10px' }}>{list.unit}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <ul className={`${styles.infoList} ${module.hFull} flx flx_col`}>
                            {defaultData2.map((list) => (
                                <li key={list.title} className={`${module.hFull} flx a_center`}>
                                    <span className={`${styles.infoListTit}`}>{list.title}</span>{' '}
                                    <span className={`${styles.infoListTxt}`}>{list.data}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className={`${module.wFull} ${module.gap20} flx flx_col`}>
                    <h3 className={`${module.subTitle} subTitle${i18n.language === 'en' ? '_en' : ''}`}>
                        {t('SignalPriorityControlStatus')}
                    </h3>

                    <div className={`${styles.infoBox} ${module.gap40} flx flx_col flx_1`}>
                        <ul className={`${styles.point} flx j_center innerBg`}>
                            {signalState.map((list) => {
                                let color = 'var(--white-50)';
                                if (list.title === '제어완료') color = 'var(--safe)';
                                else if (list.title === '제어 중') color = 'var(--caution)';

                                return (
                                    <li key={list.title}>
                                        <div className={`${module.BoxSubTit}`}>{list.title}</div>
                                        <div className="flx a_flx_end">
                                            <span className="nbr" style={{ color }}>{list.data}</span>
                                            <span className="subTxt">{list.unit}</span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>


                        <ul
                            className={`${styles.signalState} ${module.sizeFull} cst_scroll`}
                            ref={scrollContainerRef}
                        >
                            {signalState2.map((list) => (
                                <li
                                    key={list.id}
                                    className={`${module.gap40} flx`}
                                    ref={el => activeItemRefs.current[list.id] = el}
                                >
                                    <div className={`${styles.line} flx a_center j_center`}>
                                        {list.title === currentIxrName ? (
                                            <span>
                                                <img src="/images/module/emerghVeh.png" alt="제어 중" />
                                            </span>
                                        ) : (
                                            <span></span>
                                        )}
                                    </div>
                                    <div className={`${styles.list} ${module.gap60} flx a_center`}>
                                        <div className={`${module.ft40} ${module.gap20} ${styles.mark} flx a_center ${list.control === '완료' && styles.safe
                                            } ${list.control === '중' && styles.caution} ${list.control === '전' && styles.white50}`}
                                        >
                                            제어 {list.control}
                                        </div>
                                        <div>
                                            <span className={module.ft34}>{removeBracketContent(list.title)}</span>
                                            <div className={`${module.gap20} ${module.ftLight} flx`}>
                                                <span>교차로번호</span>
                                                <span>: {list.num}</span>
                                            </div>
                                        </div>
                                        <div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

// import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
// import ApiService from 'services/ApiService';
// import module from 'pages/css/moduleStyle.module.css';
// import { styled } from '@mui/material/styles';
// import styles from 'pages/css/sgnlOp.module.css';
// import Button from '@mui/material/Button';
// import { StyledIconCctvBtn } from 'components/button';
// import { useTranslation } from 'react-i18next';
// const TrafficTotalContext = createContext(null);
// const SelectedItemContext = createContext(null);

// export function SwItsEmergVehTotalProvider({ children, startDate, endDate }) {
//     const [currentData, setCurrentData] = useState();
//     const [selectedItem, setSelectedItem] = useState(null);
//     const lastSelectionRef = useRef(null);

//     const fetchData = async () => {
//         if (!startDate || !endDate) return;

//         try {
//             const response = await ApiService.get(
//                 `newdashboard/sgnloprt/GetEmergVehLog?sdtm=${startDate}&edtm=${endDate}`
//             );
//             if (response.status === 200) {
//                 const data = response.data;

//                 const vehList = [...(data.veh || [])];
//                 const runningIndex = vehList.findIndex(v => v.runYN === 'Y');
//                 if (runningIndex > -1) {
//                     const [runningVeh] = vehList.splice(runningIndex, 1);
//                     vehList.unshift(runningVeh); // 맨 앞에 삽입
//                 }

//                 setCurrentData({
//                     resultData: {
//                         ...data,
//                         veh: vehList, // 수정된 배열로 대체
//                     }
//                 });

//                 // 운행 중인 차량이 있고 기존 선택된 항목이 없거나 데이터가 변경된 경우에 항상 업데이트
//                 if (vehList.length > 0) {
//                     const runningVeh = vehList[0];

//                     // 이전 선택 항목이 없거나 ID가 다른 경우에만 새로 설정
//                     const currentVehId = `${runningVeh.deviceId}-${runningVeh.departureTime}`;

//                     if (!selectedItem || selectedItem.id !== currentVehId) {
//                         console.log('새로운 차량 정보 갱신:', runningVeh);

//                         const isComplete = runningVeh.runYN === 'N';

//                         // 기존 포맷 함수 유지
//                         const formatSeconds = (seconds) => {
//                             const min = Math.floor(seconds / 60);
//                             const sec = seconds % 60;
//                             return `${min}분 ${sec}초`;
//                         };

//                         const formatDepartureTime = (datetime) => {
//                             if (!datetime) return '';
//                             const date = new Date(datetime);
//                             return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, '0')}월 ${String(date.getDate()).padStart(2, '0')}일 ${String(date.getHours()).padStart(2, '0')}시 ${String(date.getMinutes()).padStart(2, '0')}분`;
//                         };

//                         const truncateText = (text, maxLength = 13) => {
//                             if (!text) return '';
//                             return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
//                         };

//                         setSelectedItem({
//                             id: currentVehId,
//                             title: runningVeh.serviceNm,
//                             titleRow: truncateText(runningVeh.serviceNm),
//                             state: isComplete ? '운행완료' : '운행중',
//                             complete: isComplete,
//                             stDate: formatDepartureTime(runningVeh.departureTime),
//                             address: `${runningVeh.startNm} → ${runningVeh.endNm}`,
//                             expect: formatSeconds(runningVeh.estimatedTime),
//                             arrive: isComplete ? formatSeconds(runningVeh.travelTime) : '운행중',
//                             startDate: runningVeh.departureTime?.split('T')[0],
//                             vehicleNum: runningVeh.deviceId,
//                             location: runningVeh.location,
//                             length: runningVeh.routeLength,
//                             reductionRate: runningVeh.reductionRate,
//                             ixrList: runningVeh.ixrNmList,
//                             ixrNumList: runningVeh.ixrList,
//                             sgnlLIist: runningVeh.sgnlLIist,
//                             stationNm: runningVeh.stationNm,
//                             nowIxrList: runningVeh.nowIxrList,
//                             nowIxrNmList: runningVeh.nowIxrNmList
//                         });

//                         lastSelectionRef.current = Date.now(); // 마지막 선택 시간 기록
//                     }
//                     // 같은 차량이지만 정보가 업데이트된 경우 (위치나 신호 상태 등)
//                     else if (selectedItem && selectedItem.id === currentVehId) {
//                         // 중요 필드만 변경 여부 체크 (예: 위치, 신호 리스트 등)
//                         if (selectedItem.nowIxrList !== runningVeh.nowIxrList ||
//                             selectedItem.sgnlLIist !== runningVeh.sgnlLIist ||
//                             selectedItem.complete !== (runningVeh.runYN === 'N')) {

//                             console.log('기존 차량 정보 업데이트:', runningVeh);

//                             const isComplete = runningVeh.runYN === 'N';

//                             // 기존 포맷 함수 재사용
//                             const formatSeconds = (seconds) => {
//                                 const min = Math.floor(seconds / 60);
//                                 const sec = seconds % 60;
//                                 return `${min}분 ${sec}초`;
//                             };

//                             setSelectedItem(prevItem => ({
//                                 ...prevItem,
//                                 state: isComplete ? '운행완료' : '운행중',
//                                 complete: isComplete,
//                                 arrive: isComplete ? formatSeconds(runningVeh.travelTime) : '운행중',
//                                 reductionRate: runningVeh.reductionRate,
//                                 ixrList: runningVeh.ixrNmList,
//                                 ixrNumList: runningVeh.ixrList,
//                                 sgnlLIist: runningVeh.sgnlLIist,
//                                 nowIxrList: runningVeh.nowIxrList,
//                                 nowIxrNmList: runningVeh.nowIxrNmList
//                             }));
//                         }
//                     }
//                 }
//             }
//         } catch (error) {
//             console.error("데이터 조회 중 오류 발생:", error);
//         }
//     };
//     useEffect(() => {
//         fetchData();
//         const interval = setInterval(() => {
//             fetchData(); // 주기적으로 데이터 가져오기
//         }, 10000); // 10초마다

//         return () => clearInterval(interval);
//     }, [startDate, endDate]); // 날짜 변경 시에도 다시 실행

//     return (
//         <TrafficTotalContext.Provider value={currentData}>
//             <SelectedItemContext.Provider value={{ selectedItem, setSelectedItem }}>
//                 {children}
//             </SelectedItemContext.Provider>
//         </TrafficTotalContext.Provider>
//     );
// }

// const removeBracketContent = (text) => {
//     return text?.replace(/\s*\(.*?\)/g, '') || '';
// };

// export function BodyData() {
//     const currentData = useContext(TrafficTotalContext);
//     const { selectedItem, setSelectedItem } = useContext(SelectedItemContext);
//     const formatSeconds = (seconds) => {
//         const min = Math.floor(seconds / 60);
//         const sec = seconds % 60;
//         return `${min}분 ${sec}초`;
//     };

//     const handleClick = (list) => {
//         setSelectedItem(list);  // 클릭한 항목만 선택되게 업데이트
//     };

//     const formatDepartureTime = (datetime) => {
//         if (!datetime) return '';
//         const date = new Date(datetime);
//         return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, '0')}월 ${String(date.getDate()).padStart(2, '0')}일 ${String(date.getHours()).padStart(2, '0')}시 ${String(date.getMinutes()).padStart(2, '0')}분`;
//     };

//     const truncateText = (text, maxLength = 13) => {
//         if (!text) return '';
//         return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
//     };

//     const resultList = currentData?.resultData?.veh?.map((veh) => {
//         const isComplete = veh.runYN === 'N';

//         const id = `${veh.deviceId}-${veh.departureTime}`;

//         return {
//             id,  // id 포함
//             title: veh.serviceNm,
//             titleRow: truncateText(veh.serviceNm),
//             state: isComplete ? '운행완료' : '운행중',
//             complete: isComplete,
//             stDate: formatDepartureTime(veh.departureTime),
//             address: `${removeBracketContent(veh.startNm)} → ${removeBracketContent(veh.endNm)}`,
//             expect: formatSeconds(veh.estimatedTime),
//             arrive: isComplete ? formatSeconds(veh.travelTime) : '운행중',
//             startDate: veh.departureTime?.split('T')[0],
//             vehicleNum: veh.deviceId,
//             location: veh.location,
//             length: veh.routeLength,
//             reductionRate: veh.reductionRate,
//             ixrList: veh.ixrNmList,
//             ixrNumList: veh.ixrList,
//             sgnlLIist: veh.sgnlLIist,
//             stationNm: veh.stationNm,
//             nowIxrList: veh.nowIxrList,
//             nowIxrNmList: veh.nowIxrNmList
//         };
//     }) || [];

//     if (!currentData || resultList.length === 0) {
//         return (
//             <ul className={`${styles.listWrap} flx_col gap20`}>
//                 <li>데이터가 없습니다 !!!</li>
//             </ul>
//         );
//     }

//     return (

//         <ul className="listWrap flx_col gap20">
//             {resultList.map((list) => {
//                 const isSelected = selectedItem?.id === list.id;

//                 return (
//                     <li
//                         key={list.id}
//                         className={`${styles.selectList} ${isSelected ? styles.active : ''}`}
//                     >
//                         <button type="button" className="flx a_center" onClick={() => handleClick(list)}>
//                             <div className={`${styles.state} ${list.complete && styles.complete} flx a_center j_center`}>
//                                 {list.state}
//                             </div>
//                             <div className={`${styles.address} flx flx_col a_flx_st`}>
//                                 <span className={`${module.ft32} ${module.ftMedium}`}>[ {list.stDate} ] {list.titleRow}</span>
//                                 <span className={`${module.ftRegular} ${module.white80}`}>{list.address}</span>
//                             </div>
//                             <div className={`${styles.time} flx flx_col a_flx_end`}>
//                                 <span className={`${module.ftMedium}`}>예측시간 : {list.expect}</span>
//                                 <span className={`${styles.safe}`}>도착시간: {list.arrive}</span>
//                             </div>
//                         </button>
//                     </li>
//                 );
//             })}
//         </ul>
//     );
// }

// // 총 운행결과 
// export function HeaderData() {
//     const currentData = useContext(TrafficTotalContext);
//     if (!currentData) return null;
//     const pointResult = currentData
//         ? [
//             {
//                 title: '운행중',
//                 data: currentData.resultData.runVehCount,
//                 unit: '건',
//                 color: 'alert50'
//             },
//             {
//                 title: '운행완료',
//                 data: currentData.resultData.stopVehCount,
//                 unit: '건',
//                 color: 'white50'
//             },
//             {
//                 title: '전체 단축율',
//                 data: currentData.resultData.totalReductionRate,
//                 unit: '%',
//                 color: 'pr50'
//             }
//         ]
//         : [];
//     return (
//         <>
//             <ul className={`${styles.point} flx j_center innerBg`}>
//                 {pointResult.map((list) => {
//                     let color = '#E23A56';
//                     if (list.title === '운행완료') color = 'var(--white-50)';
//                     else if (list.title === '전체 단축율') color = 'var(--safe)';

//                     return (
//                         <li key={list.title}>
//                             <div className={`${module.BoxSubTit}`}>{list.title}</div>
//                             <div className="flx a_flx_end">
//                                 <span className='nbr' style={{ color }} >{list.data?.toLocaleString()}</span>
//                                 <span className='subTxt'>{list.unit}</span>
//                             </div>
//                         </li>
//                     );
//                 })}
//             </ul>
//         </>
//     );
// }

// export function BodyListDetail() {
//     const { selectedItem } = useContext(SelectedItemContext);
//     console.log('selectedItem :::', selectedItem)
//     const scrollContainerRef = useRef(null);
//     const activeItemRefs = useRef({});
//     const { t, i18n } = useTranslation();
//     useEffect(() => {
//         if (selectedItem && scrollContainerRef.current) {
//             const signalValues = selectedItem.sgnlLIist?.split(',').map(Number) || [];
//             const intersectionNames = selectedItem.ixrList?.split(',') || [];

//             const signalState2 = intersectionNames.map((name, idx) => ({
//                 title: name,
//                 control: signalValues[idx] === 1 ? '중' : '전',
//                 id: `signal-${idx}`,
//                 isActive: signalValues[idx] === 1
//             }));

//             // 제어 중인(값이 1인) 첫 번째 항목 찾기
//             const activeItemIndex = signalState2.findIndex(item => item.isActive);

//             if (activeItemIndex !== -1) {
//                 const activeItemId = signalState2[activeItemIndex].id;
//                 const activeItemElement = activeItemRefs.current[activeItemId];

//                 if (activeItemElement) {
//                     // 해당 항목으로 스크롤
//                     activeItemElement.scrollIntoView({
//                         behavior: 'smooth',
//                         block: 'center'
//                     });
//                 }
//             }
//         }
//     }, [selectedItem]);

//     if (!selectedItem) return <div>선택된 항목이 없습니다.</div>;
//     console.log('selectedItem.complete ;:::', selectedItem.complete)
//     if (selectedItem.complete) {
//         selectedItem.sgnlLIist = selectedItem.sgnlLIist
//             ?.split(',')
//             .map(val => val === '1' ? '0' : val)
//             .join(',');

//         selectedItem.nowIxrList = "";
//         selectedItem.nowIxrNmList = "";
//     }

//     function smartSplit(str) {
//         const result = [];
//         let current = '';
//         let insideParen = false;

//         for (let i = 0; i < str.length; i++) {
//             const char = str[i];

//             if (char === '(') insideParen = true;
//             if (char === ')') insideParen = false;

//             if (char === ',' && !insideParen) {
//                 result.push(current.trim());
//                 current = '';
//             } else {
//                 current += char;
//             }
//         }

//         if (current.trim()) result.push(current.trim());
//         return result;
//     }

//     const intersectionNames = selectedItem.ixrList ? smartSplit(selectedItem.ixrList) : [];
//     console.log('intersectionNames :::', intersectionNames)
//     const intersectionNum = selectedItem.ixrNumList?.split(',') || [];
//     const signalValues = selectedItem.sgnlLIist?.split(',').map(Number) || [];

//     const nowIxrNmList = selectedItem.nowIxrNmList?.split(',') || [];
//     const currentIxrName = nowIxrNmList[0]; // 차량 마커를 표시할 지점 이름

//     const defaultData = [
//         {
//             title: '기존 예상 도착시간',
//             data: selectedItem.expect,
//             color: 'white50'
//         },
//         {
//             title: selectedItem.complete ? '도착시간' : '신호 제어 예상 도착시간',
//             data: selectedItem.arrive,
//         },
//         {
//             title: selectedItem.complete ? '단축율' : '전체 예상 단축율',
//             data: (!selectedItem.complete && Number(selectedItem.reductionRate) === 0) ? '-' : selectedItem.reductionRate,
//             unit: selectedItem.complete ? '%' : (Number(selectedItem.reductionRate) === 0 ? '' : '%'),
//             class: true
//         }
//     ];

//     const defaultData2 = [
//         {
//             title: '차량번호',
//             data: selectedItem.vehicleNum || '알 수 없음'
//         },
//         {
//             title: '관할기관',
//             data: selectedItem.stationNm || '알 수 없음'
//         },
//         {
//             title: '목적지',
//             data: selectedItem.title || '알 수 없음'
//         },
//         {
//             title: '출발시간',
//             data: selectedItem.stDate || '알 수 없음'
//         },
//         {
//             title: '운행경로',
//             data: selectedItem.address || '알 수 없음'
//         },
//         {
//             title: '운행거리',
//             data: `${selectedItem.length}km` || '알 수 없음'
//         }
//     ];
//     const nowIxrList = selectedItem.nowIxrList?.split(',') || []; // 번호 기준으로 비교

//     const signalState2 = intersectionNames.map((name, idx) => {
//         const signal = signalValues[idx];
//         const ixrCode = intersectionNum[idx]; // 번호로 판단
//         const isNow = nowIxrList.includes(ixrCode); // 번호 기준 비교

//         let controlStatus = '전';

//         if (signal === 1) {
//             controlStatus = '중'; // 제어 중이면 무조건 중
//         } else if (!isNow) {
//             controlStatus = '완료'; // 제어하지 않았고, 현재 지점에 없다면 완료
//         }

//         return {
//             title: name,
//             control: controlStatus,
//             id: `signal-${idx}`,
//             isActive: controlStatus === '중',
//             num: ixrCode
//         };
//     });

//     const totalComplete = signalState2.filter(item => item.control === '완료').length;
//     const totalDuring = signalState2.filter(item => item.control === '중').length;
//     const totalBefore = signalState2.filter(item => item.control === '전').length;

//     const signalState = [
//         {
//             title: '제어완료',
//             data: totalComplete,
//             unit: '개',
//             color: 'white50'
//         },
//         {
//             title: '제어 중',
//             data: totalDuring,
//             unit: '개',
//             color: 'caution'
//         },
//         {
//             title: '제어 전',
//             data: totalBefore,
//             unit: '개',
//             color: 'white50'
//         },
//     ];

//     return (
//         <div className={`${module.gap40} flx flx_col inner L_l`}>
//             <div className={`${module.gap40} titWrap flx a_center`}>
//                 <h2 className="boxTit" style={{ color: 'var(--iv-02)' }}>{selectedItem.title}</h2>
//                 <div className={`${styles.state} ${selectedItem.complete && styles.complete} flx a_center j_center`}>
//                     {selectedItem.state}
//                 </div>
//             </div>

//             <div className={`${module.gap60} flx flx_1`} style={{ height: 'calc(100% - 68px - 40px)' }}>
//                 {/* 기본정보 */}
//                 <div className={`${module.wFull} ${module.gap20} flx flx_col`}>
//                     <h3 className={`${module.subTitle} subTitle${i18n.language === 'en' ? '_en' : ''}`}>
//                         {t('EmergencyVehicleProfile')}
//                     </h3>
//                     <div className={`${styles.infoBox} ${module.gap40} flx flx_col`}>
//                         <ul className={`${styles.point} flx j_center innerBg`}>
//                             {defaultData.map((list) => (
//                                 <li key={list.title}>
//                                     <div className={`${module.BoxSubTit}`}>{list.title}</div>
//                                     <div className="flx a_flx_end">
//                                         <span className={`${styles[list.color]} ${module.ft54}`}>{list.data}</span>
//                                         <span className='subTxt' style={{ marginBottom: '10px' }}>{list.unit}</span>
//                                     </div>
//                                 </li>
//                             ))}
//                         </ul>

//                         <ul className={`${styles.infoList} ${module.hFull} flx flx_col`}>
//                             {defaultData2.map((list) => (
//                                 <li key={list.title} className={`${module.hFull} flx a_center`}>
//                                     <span className={`${styles.infoListTit}`}>{list.title}</span>{' '}
//                                     <span className={`${styles.infoListTxt}`}>{list.data}</span>
//                                 </li>
//                             ))}
//                         </ul>
//                     </div>
//                 </div>

//                 <div className={`${module.wFull} ${module.gap20} flx flx_col`}>
//                     <h3 className={`${module.subTitle} subTitle${i18n.language === 'en' ? '_en' : ''}`}>
//                         {t('SignalPriorityControlStatus')}
//                     </h3>

//                     <div className={`${styles.infoBox} ${module.gap40} flx flx_col flx_1`}>
//                         <ul className={`${styles.point} flx j_center innerBg`}>
//                             {signalState.map((list) => {
//                                 let color = 'var(--white-50)';
//                                 if (list.title === '제어완료') color = 'var(--safe)';
//                                 else if (list.title === '제어 중') color = 'var(--caution)';

//                                 return (
//                                     <li key={list.title}>
//                                         <div className={`${module.BoxSubTit}`}>{list.title}</div>
//                                         <div className="flx a_flx_end">
//                                             <span className="nbr" style={{ color }}>{list.data}</span>
//                                             <span className="subTxt">{list.unit}</span>
//                                         </div>
//                                     </li>
//                                 );
//                             })}
//                         </ul>


//                         <ul
//                             className={`${styles.signalState} ${module.sizeFull} cst_scroll`}
//                             ref={scrollContainerRef}
//                         >
//                             {signalState2.map((list) => (
//                                 <li
//                                     key={list.id}
//                                     className={`${module.gap40} flx`}
//                                     ref={el => activeItemRefs.current[list.id] = el}
//                                 >
//                                     <div className={`${styles.line} flx a_center j_center`}>
//                                         {list.title === currentIxrName ? (
//                                             <span>
//                                                 <img src="/images/module/emerghVeh.png" alt="제어 중" />
//                                             </span>
//                                         ) : (
//                                             <span></span>
//                                         )}
//                                     </div>
//                                     <div className={`${styles.list} ${module.gap60} flx a_center`}>
//                                         <div className={`${module.ft40} ${module.gap20} ${styles.mark} flx a_center ${list.control === '완료' && styles.safe
//                                             } ${list.control === '중' && styles.caution} ${list.control === '전' && styles.white50}`}
//                                         >
//                                             제어 {list.control}
//                                         </div>
//                                         <div>
//                                             <span className={module.ft34}>{removeBracketContent(list.title)}</span>
//                                             <div className={`${module.gap20} ${module.ftLight} flx`}>
//                                                 <span>교차로번호</span>
//                                                 <span>: {list.num}</span>
//                                             </div>
//                                         </div>
//                                         <div>
//                                         </div>
//                                     </div>
//                                 </li>
//                             ))}
//                         </ul>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }
